import { DEFAULT_RATIO, OVERLAY_GAP, RATIO_HEIGHT } from './constants';
import { getMaxRange } from './get-max-range';
import { floorVideoHeight } from './round-video-size';
import { Area, Size } from './types';

export interface GridLayout {
    participantsListLayout: Area[];
}

interface ParamsForCount {
    rowsCount: number;
    colCount: number;
    videoWidth: number;
    videoHeight: number;
}

const oldGetBestParamsForCount = (size: Size, count: number, ratio: number): Maybe<ParamsForCount> => {
    if (!size.width || !size.height || count < 1) {
        return;
    }

    let bestRowCount = 0;
    let bestColCount = 0;
    let bestVideoWidth = 0;

    // Высчитываем кол-во строк/колонок, при которых видео будут максимального размера
    for (let colCount = 1; colCount <= count; colCount++) {
        const rowCount = Math.ceil(count / colCount);

        const rowHeight = size.height / rowCount;
        const colWidth = size.width / colCount;

        // Высчитываем, какого размера видео с фиксированным соотношением сторон поместится в данную ячейку
        const finalVideoWidth = Math.min(colWidth, rowHeight * ratio);

        if (finalVideoWidth > bestVideoWidth) {
            bestColCount = colCount;
            bestRowCount = rowCount;
            bestVideoWidth = finalVideoWidth;
        }
    }

    return {
        rowsCount: bestRowCount,
        colCount: bestColCount,
        videoWidth: bestVideoWidth,
        videoHeight: bestVideoWidth / ratio,
    };
};

const newGetBestParamsForCount = (size: Size, count: number, ratio: number, margin = 0): Maybe<ParamsForCount> => {
    if (!size.width || !size.height || count < 1) {
        return;
    }

    let bestRowCount = 0;
    let bestColCount = 0;
    let bestVideoWidth = 0;
    const containerSize = {
        width: size.width - margin * 2,
        height: size.height - margin * 2,
    };
    // Высчитываем кол-во строк/колонок, при которых видео будут максимального размера
    for (let colCount = 1; colCount <= count; colCount++) {
        const rowCount = Math.ceil(count / colCount);

        const rowHeight = (containerSize.height - (rowCount - 1) * OVERLAY_GAP) / rowCount;
        const colWidth = (containerSize.width - (colCount - 1) * OVERLAY_GAP) / colCount;
        // Высчитываем, какого размера видео с фиксированным соотношением сторон поместится в данную ячейку
        const finalVideoWidth = Math.min(colWidth, rowHeight * ratio);

        if (finalVideoWidth > bestVideoWidth) {
            bestColCount = colCount;
            bestRowCount = rowCount;
            bestVideoWidth = finalVideoWidth;
        }
    }
    const bestVideoHeight = bestVideoWidth / ratio;
    const { min: minHeight, max: maxHeight } = getMaxRange(bestVideoHeight);
    const videoHeight = Math.max(Math.min(bestVideoHeight, maxHeight), minHeight);

    const roundedVideoHeight = floorVideoHeight(videoHeight, ratio === DEFAULT_RATIO ? RATIO_HEIGHT : 1); // высота должна быть кратна RATIO_HEIGHT

    return {
        rowsCount: bestRowCount,
        colCount: bestColCount,
        videoWidth: roundedVideoHeight * ratio,
        videoHeight: roundedVideoHeight,
    };
};

/**
 * @param {{ width: number, height: number }} size
 * @param {number} realCount Реальное кол-во участников, которые будут отображены в сетке
 * @param {number} placeCount Под сколько участников зарезервировано места
 * @param {number} ratio Соотношение сторон каждого элемента сетки
 * @returns {Object}
 */
function oldGetGridLayout(
    size: Size,
    realCount: number,
    placeCount: number,
    ratio: number = DEFAULT_RATIO
): GridLayout {
    const result: Area[] = [];

    const params = oldGetBestParamsForCount(size, placeCount, ratio);

    if (!params) {
        return { participantsListLayout: result };
    }

    const { rowsCount, colCount, videoWidth, videoHeight } = params;

    const verticalPadding = (size.height - rowsCount * videoHeight) / 2;
    const realRowsCount = Math.ceil(realCount / colCount);

    for (let y = 0; y < realRowsCount; y++) {
        // В общем хитрая логика. На последней странице участников мы резервируем место под максимальное кол-во
        // участников, а показываем сколько нужно. В первом ряду мы показываем участников последовательно слева
        // направо. То есть одного участника покажем слева, второго справа от него и так по мере их появления в
        // конференции. Ниже сетка 3x3
        // ===================    ===================    ===================
        // |  1  |           |    |  1  |  2  |     |    |  1  |  2  |  3  |
        // -------           |    |------------     |    |-----------------|
        // |                 |    |                 |    |                 |
        // |                 |    |                 |    |                 |
        // ===================    ===================    ===================
        // Когда первый ряд заполнен, то в других рядах участников мы должны отображать уже отцентрировано
        // ===================    ===================    ===================    ===================
        // |  1  |  2  |  3  |    |  1  |  2  |  3  |    |  1  |  2  |  3  |    |  1  |  2  |  3  |
        // |-----------------|    |-----------------|    |-----------------|    |-----------------|
        // |     |  4  |     |    |  |  4  |  5  |  |    |  4  |  5  |  6  |    |  4  |  5  |  6  |
        // |     -------     |    |  -------------  |    |-----------------|    |-----------------|
        // |                 |    |                 |    |                 |    |     |  7  |     |
        // ===================    ===================    ===================    ===================
        const isRealLastRow = y > 0 && y === realRowsCount - 1;
        const itemsCountInRow = isRealLastRow ? realCount - y * colCount : colCount;
        const horizontalPadding = (size.width - itemsCountInRow * videoWidth) / 2;

        for (let x = 0; x < itemsCountInRow; x++) {
            result.push({
                x: horizontalPadding + x * videoWidth,
                y: verticalPadding + y * videoHeight,
                width: videoWidth,
                height: videoHeight,
            });
        }
    }

    return { participantsListLayout: result };
}
function newGetGridLayout(
    size: Size,
    realCount: number,
    placeCount: number,
    ratio: number = DEFAULT_RATIO,
    margin = 0
): GridLayout {
    const result: Area[] = [];

    const params = newGetBestParamsForCount(size, placeCount, ratio, margin);
    if (!params) {
        return { participantsListLayout: result };
    }

    const { rowsCount, colCount, videoWidth, videoHeight } = params;

    const containerSize = {
        width: size.width - margin * 2,
        height: size.height - margin * 2,
    };

    const verticalPadding = (containerSize.height - rowsCount * videoHeight - (rowsCount - 1) * OVERLAY_GAP) / 2;
    const realRowsCount = Math.ceil(realCount / colCount);

    for (let y = 0; y < realRowsCount; y++) {
        // В общем хитрая логика. На последней странице участников мы резервируем место под максимальное кол-во
        // участников, а показываем сколько нужно. В первом ряду мы показываем участников последовательно слева
        // направо. То есть одного участника покажем слева, второго справа от него и так по мере их появления в
        // конференции. Ниже сетка 3x3
        // ===================    ===================    ===================
        // |  1  |           |    |  1  |  2  |     |    |  1  |  2  |  3  |
        // -------           |    |------------     |    |-----------------|
        // |                 |    |                 |    |                 |
        // |                 |    |                 |    |                 |
        // ===================    ===================    ===================
        // Когда первый ряд заполнен, то в других рядах участников мы должны отображать уже отцентрировано
        // ===================    ===================    ===================    ===================
        // |  1  |  2  |  3  |    |  1  |  2  |  3  |    |  1  |  2  |  3  |    |  1  |  2  |  3  |
        // |-----------------|    |-----------------|    |-----------------|    |-----------------|
        // |     |  4  |     |    |  |  4  |  5  |  |    |  4  |  5  |  6  |    |  4  |  5  |  6  |
        // |     -------     |    |  -------------  |    |-----------------|    |-----------------|
        // |                 |    |                 |    |                 |    |     |  7  |     |
        // ===================    ===================    ===================    ===================
        const isRealLastRow = y > 0 && y === realRowsCount - 1;
        const itemsCountInRow = isRealLastRow ? realCount - y * colCount : colCount;
        const horizontalPadding =
            (containerSize.width - itemsCountInRow * videoWidth - (itemsCountInRow - 1) * OVERLAY_GAP) / 2;

        for (let x = 0; x < itemsCountInRow; x++) {
            result.push({
                x: horizontalPadding + x * videoWidth + x * OVERLAY_GAP + margin,
                y: verticalPadding + y * videoHeight + y * OVERLAY_GAP + margin,
                width: videoWidth,
                height: videoHeight,
            });
        }
    }

    return { participantsListLayout: result };
}

export const getGridLayout = (
    size: Size,
    realCount: number,
    placeCount: number,
    ratio: number = DEFAULT_RATIO,
    isNewGrid = false,
    margin = 0
): GridLayout => {
    if (isNewGrid) {
        return newGetGridLayout(size, realCount, placeCount, ratio, margin);
    } else {
        return oldGetGridLayout(size, realCount, placeCount, ratio);
    }
};
